import { type Device, type DeviceHome, DeviceStatus } from '@lmc/ts-common'
import { ConfirmationModal } from '@lmc/ui-kit'
import { Button, Text } from '@lmc/ui-kit/src/general'
import Table from '@lmc/ui-kit/src/molecules/Table/Table'
import { NAMESPACE } from 'locale'
import { dacConstants } from 'locale/deviceAccessControl'
import { generalConstants } from 'locale/general'
import type { Dispatch, FC, SetStateAction } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { getBadgeType } from '../../utils'

type DevicesManageTableProps = {
  device: Device
  updateDevice: ({
    homes,
    id
  }: {
    id?: string
    homes: { home: string; status: DeviceStatus }[]
  }) => void
  id?: string
  isLoading?: boolean
  isLoadingUpdateDevice?: boolean
  isInlineEditing?: boolean
  selectedHomes: string[]
  setSelectedHomes: Dispatch<SetStateAction<string[]>>
  hasGroupAccess?: boolean
  homeId?: string
  testId?: string
}

const DevicesManageTable: FC<DevicesManageTableProps> = ({
  device,
  updateDevice,
  id,
  isLoading,
  isLoadingUpdateDevice,
  isInlineEditing,
  selectedHomes,
  setSelectedHomes,
  hasGroupAccess,
  homeId,
  testId
}) => {
  const { t } = useTranslation(NAMESPACE.DEVICE_ACCESS_CONTROL)

  const getRows = () =>
    device?.homes?.map((home: DeviceHome) => {
      const isCurrentSite = home.home.id === homeId

      return {
        name: { name: home.home.name, isCurrentSite },
        status: home.status,
        action: { home: home.home.id, status: home.status },
        key: home.home.id
      }
    })

  const handleChangeStatus = (status: DeviceStatus, home: string) =>
    updateDevice({
      id,
      homes: [{ status, home }]
    })

  const columns = [
    {
      dataIndex: 'name',
      title: t(dacConstants.SITE_NAME),
      render: ({
        name,
        isCurrentSite
      }: {
        name: string
        isCurrentSite: boolean
      }) => (
        <StyledNameWrapper>
          <Text>{name}</Text>
          {isCurrentSite && (
            <StyledCurrentSiteWrapper>
              {t(dacConstants.CURRENT_SITE)}
            </StyledCurrentSiteWrapper>
          )}
        </StyledNameWrapper>
      )
    },
    {
      dataIndex: 'status',
      title: t(dacConstants.STATUS),
      render: (status: DeviceStatus) => getBadgeType(status, t)
    },
    {
      dataIndex: 'action',
      title: t(dacConstants.ACTION),
      render: ({ home, status }: { home: string; status: DeviceStatus }) => (
        <ActionButtonsWrapper>
          {(status === DeviceStatus.approved ||
            status === DeviceStatus.pending) && (
            <ConfirmationModal
              localisation={{
                confirmText: t(generalConstants.CONFIRM),
                title: t(dacConstants.BLOCK_DEVICE_ALL),
                cancelText: t(generalConstants.CANCEL)
              }}
              modalContent={
                <ModalContentWrapper>
                  <Text level={1}>{t(dacConstants.BLOCK_DEVICE_ALL_DESC)}</Text>
                  <StyledTextDesc level={1}>
                    {t(dacConstants.ARE_YOU_SURE_YOU_WANT_BLOCK_THIS_SITE)}
                  </StyledTextDesc>
                </ModalContentWrapper>
              }
            >
              <StyledButton
                disabled={isInlineEditing}
                variant="text"
                onClick={() => handleChangeStatus(DeviceStatus.blocked, home)}
                data-cy="btn-block"
              >
                {t(dacConstants.BLOCK)}
              </StyledButton>
            </ConfirmationModal>
          )}
          {(status === DeviceStatus.blocked ||
            status === DeviceStatus.pending) && (
            <ConfirmationModal
              localisation={{
                confirmText: t(generalConstants.CONFIRM),
                title: t(dacConstants.APPROVE_DEVICE_ALL),
                cancelText: t(generalConstants.CANCEL)
              }}
              confirmButtonProps={{ variant: 'primary' }}
              modalContent={
                <ModalContentWrapper>
                  <Text level={1}>
                    {t(dacConstants.APPROVE_DEVICE_ALL_DESC)}
                  </Text>
                  <StyledTextDesc level={1}>
                    {t(dacConstants.ARE_YOU_SURE_YOU_WANT_APPROVE_THIS_SITE)}
                  </StyledTextDesc>
                </ModalContentWrapper>
              }
            >
              <Button
                disabled={isInlineEditing}
                variant="text"
                onClick={() => handleChangeStatus(DeviceStatus.approved, home)}
                data-cy="btn-approve"
              >
                {t(dacConstants.APPROVE)}
              </Button>
            </ConfirmationModal>
          )}
        </ActionButtonsWrapper>
      )
    }
  ]

  return (
    <Table
      loading={isLoading || isLoadingUpdateDevice}
      rowSelection={
        hasGroupAccess
          ? {
              type: 'checkbox',
              onChange: (selectedRowKeys: React.Key[]) => {
                setSelectedHomes(selectedRowKeys as string[])
              },
              selectedRowKeys: selectedHomes,
              getCheckboxProps: () => ({
                disabled: isInlineEditing
              })
            }
          : undefined
      }
      pagination={false}
      columns={columns}
      dataSource={getRows()}
      data-testid={testId}
    />
  )
}

const ModalContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const ActionButtonsWrapper = styled.div`
  width: 120px;
  display: flex;
  justify-content: space-between;
`

const StyledButton = styled(Button)`
  color: ${(props) => props.theme.color.status.danger};
`

const StyledTextDesc = styled(Text)`
  font-weight: 500;
`

const StyledCurrentSiteWrapper = styled(Text)`
  margin-left: 5px;
  color: ${(props) => props.theme.color.neutral.color5};
`

const StyledNameWrapper = styled.div`
  display: flex;
`

export default DevicesManageTable
